import React, { useEffect, useRef, useState } from "react";
import { Avatar, Select, Space, Spin, Typography } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { errorMessage, searchProfessionals } from "./pagesApi";

const { Text } = Typography;

/** Bulgarian role names, as the homepage row labels them. */
const ROLE_LABELS = {
  VET: "Ветеринарен лекар",
  CLINIC: "Клиника",
  GROOMER: "Грумър",
  TRAINER: "Треньор",
  HOTELS: "Хотел за любимци",
  PET_SITTER: "Гледач",
  PET_WALKER: "Разхождач",
};

export const roleLabel = (role) => ROLE_LABELS[role] || role || "";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/**
 * Search-and-add for the homepage's featured professionals (DAB-852).
 *
 * Replaces typing 24-character ids into a comma-separated text field. Offers
 * only professionals the row would actually show (the API filters approved,
 * active, public roles), with a photo and role so two people with the same
 * name can be told apart. Selecting adds; the Select itself never holds a
 * value, so it is ready for the next search straight away.
 */
export default function ProfessionalPicker({ onSelect, excludeIds = [], disabled }) {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  // Only the latest search may land: a slow early response must not replace
  // the results of what was typed after it.
  const latest = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onSearch = (text) => {
    setQuery(text);
    setError(null);
    clearTimeout(timer.current);
    const term = text.trim();
    const token = ++latest.current;
    if (term.length < MIN_QUERY_LENGTH) {
      setRows([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const found = await searchProfessionals(term);
        if (token === latest.current) setRows(found || []);
      } catch (searchError) {
        if (token === latest.current) {
          setRows([]);
          setError(errorMessage(searchError));
        }
      } finally {
        if (token === latest.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);
  };

  const select = (id) => {
    const row = rows.find((candidate) => candidate.id === id);
    if (row) onSelect(row);
    setQuery("");
    setRows([]);
  };

  const notFound = (() => {
    if (isLoading) return <Spin size="small" />;
    if (error) return `Търсенето не успя: ${error}`;
    if (query.trim().length < MIN_QUERY_LENGTH) return "Въведи поне 2 букви от името или имейла";
    return "Няма намерени професионалисти";
  })();

  return (
    <Select
      showSearch
      value={null}
      searchValue={query}
      placeholder="Добави професионалист — търси по име или имейл"
      filterOption={false}
      onSearch={onSearch}
      onChange={select}
      notFoundContent={notFound}
      disabled={disabled}
      size="large"
      className="mp-professional-picker"
      options={rows
        .filter((row) => !excludeIds.includes(row.id))
        .map((row) => ({
          value: row.id,
          label: (
            <Space size={8}>
              <Avatar size="small" src={row.photo || undefined} icon={<UserOutlined />} />
              <span>{row.name}</span>
              <Text type="secondary">{roleLabel(row.role)}</Text>
            </Space>
          ),
        }))}
    />
  );
}
